import React from 'react';
import { useHBUI } from '../../components/HBUIProvider/HBUIProvider';
import { defaultResolveLink } from './defaultResolveLink';
import type {
    HBLinkTarget,
    HBResolvedLink,
    UseLinkBehaviorLinkProps,
    UseLinkBehaviorOptions,
    UseLinkBehaviorResult
} from './useLinkBehavior.types';

/** Attribute that marks a link rendered through `LinkComponent`. `LinkDriver` reads it. */
export const LINK_ROUTED_ATTRIBUTE = 'data-routed';

const NOT_A_LINK: UseLinkBehaviorResult = {
    isLink: false,
    as: 'a',
    href: undefined,
    linkProps: undefined
};

/**
 * Destinations already warned about, so a list of links logs once per destination rather than
 * once per link. Dev-only, so it never grows in production.
 */
const warnedMissingLinkComponent = new Set<string>();

/**
 * Warns once per destination when an in-app target has no `LinkComponent`, so the silent
 * full-page-load fallback is visible.
 *
 * Runs in an effect rather than in render: links render inside rows that re-render on every
 * keystroke, and a render pass React discards would mark the destination warned without ever
 * logging it.
 */
function useMissingLinkComponentWarning(
    destination: string | undefined,
    componentName: string,
    hasFallbackHref: boolean
) {
    React.useEffect(() => {
        if (
            process.env.NODE_ENV === 'production' ||
            !destination ||
            warnedMissingLinkComponent.has(destination)
        ) {
            return;
        }

        warnedMissingLinkComponent.add(destination);
        // eslint-disable-next-line no-console -- dev-only diagnostic for a silent full-page-load fallback
        console.warn(
            hasFallbackHref
                ? `${componentName}: an in-app link to ${destination} has no \`LinkComponent\` (from HBUIProvider). Falling back to a native anchor, so client-side navigation will not occur.`
                : `${componentName}: an in-app link to ${destination} has no \`LinkComponent\` (from HBUIProvider) and no resolved URL, so it cannot navigate. Rendering it as a non-link.`
        );
    }, [destination, componentName, hasFallbackHref]);
}

const stopDisabledClick = (event: React.MouseEvent) => event.stopPropagation();

const warnedButtonRole = new Set<string>();

/** Warns once per component when a link is given `role="button"`, which the hook ignores. Dev-only. */
function useButtonRoleWarning(isButtonRole: boolean, componentName: string) {
    React.useEffect(() => {
        if (
            process.env.NODE_ENV === 'production' ||
            !isButtonRole ||
            warnedButtonRole.has(componentName)
        ) {
            return;
        }

        warnedButtonRole.add(componentName);
        // eslint-disable-next-line no-console -- dev-only diagnostic for a role the hook ignores
        console.warn(
            `${componentName}: a link with a destination cannot have role="button", so the role was ignored. Use a button for an action.`
        );
    }, [isButtonRole, componentName]);
}

const isModifiedClick = (event: React.MouseEvent) =>
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

/**
 * Link behavior shared by every component that can navigate: which element to render, the props
 * for it, and the click rules that make right-click, Cmd/Ctrl-click and middle-click work.
 *
 * The component keeps its own root element and styles. It renders `as` with `linkProps` when
 * `isLink` is true and its own element otherwise.
 *
 * - `to` is classified by `HBUIProvider`'s `resolveLink`. In-app targets render `LinkComponent`
 *   (the `LinkComponent` option first, then the provider's) with `resolved.to`, or the original
 *   `to` when the resolver gives none. External ones render a plain anchor. `href` is a deprecated native anchor
 *   that is never routed. `to` wins when both are set.
 * - A link that goes somewhere owns its click: it stops propagation so a parent row never acts
 *   on it. Cmd, Ctrl, Shift and Alt clicks skip `onClick` and are left to the browser.
 * - A link with no real destination (`href` of `#`) lets the click bubble. A disabled link stops
 *   it without running `onClick`, like a disabled `<button>`, so a parent row never acts on it.
 * - A route object that resolves to no URL and has no `LinkComponent` cannot navigate, so it is
 *   not a link, disabled or not: `isLink` is false and the component renders its own element.
 * - `download` always renders a plain anchor, since a router `Link` would navigate instead.
 * - There is no key handler, so Enter activates once natively and Space does nothing.
 */
export function useLinkBehavior({
    to,
    href,
    target,
    rel,
    download,
    onClick,
    disabled = false,
    role: requestedRole,
    tabIndex,
    LinkComponent: linkComponentOption,
    componentName
}: UseLinkBehaviorOptions): UseLinkBehaviorResult {
    const { LinkComponent: contextLinkComponent, resolveLink } = useHBUI();
    const LinkComponent = linkComponentOption ?? contextLinkComponent;
    const role = requestedRole === 'button' ? undefined : requestedRole;

    // `to` and `href` are normalised so an empty string counts as unset: `href=""` would
    // otherwise win the fallback and render `<a href="">`, which reloads the current page.
    const routedTarget: HBLinkTarget | undefined = to || undefined;
    const nativeHref =
        routedTarget === undefined ? href || undefined : undefined;
    const destination = routedTarget ?? nativeHref;

    let resolved: HBResolvedLink | undefined;
    if (routedTarget !== undefined) {
        resolved = (resolveLink ?? defaultResolveLink)(routedTarget);
    } else if (nativeHref !== undefined) {
        resolved = { href: nativeHref, isInternal: false };
    }

    // `LinkComponent`s such as React Router's `Link` ignore `download`, so a download is always a
    // plain anchor.
    const isDownload = download !== undefined && download !== false;
    // A link with no destination is not a link here, so its role is the component's own business.
    // `#` is an action link, which is also not worth a warning.
    useButtonRoleWarning(
        requestedRole === 'button' &&
            destination !== undefined &&
            resolved?.href !== '#',
        componentName
    );
    const shouldWarn =
        !!resolved?.isInternal && !LinkComponent && !disabled && !isDownload;
    useMissingLinkComponentWarning(
        shouldWarn
            ? typeof destination === 'string'
                ? destination
                : JSON.stringify(destination)
            : undefined,
        componentName,
        !!resolved?.href
    );

    if (!resolved || destination === undefined) {
        return NOT_A_LINK;
    }

    const isRouted = resolved.isInternal && !!LinkComponent && !isDownload;

    if (!isRouted && !resolved.href) {
        return NOT_A_LINK;
    }

    if (disabled) {
        return {
            isLink: true,
            as: 'a',
            href: undefined,
            linkProps: {
                role: role ?? 'link',
                tabIndex: tabIndex ?? 0,
                'aria-disabled': true,
                onClick: stopDisabledClick
            }
        };
    }

    const hasDestination =
        typeof destination === 'object' ||
        (!!resolved.href && resolved.href !== '#');

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!hasDestination) {
            onClick?.(event);
            return;
        }

        event.stopPropagation();

        if (isModifiedClick(event)) {
            return;
        }

        onClick?.(event);
    };

    const linkProps: UseLinkBehaviorLinkProps = {
        ...(isRouted
            ? {
                  to: resolved.to ?? destination,
                  [LINK_ROUTED_ATTRIBUTE]: 'true' as const
              }
            : resolved.href !== undefined && { href: resolved.href }),
        ...(target !== undefined && { target }),
        ...((rel !== undefined || target === '_blank') && {
            rel: rel ?? 'noopener noreferrer'
        }),
        ...(download !== undefined && { download }),
        ...(role !== undefined && { role }),
        ...(tabIndex !== undefined && { tabIndex }),
        onClick: handleClick
    };

    return {
        isLink: true,
        as: isRouted ? (LinkComponent as React.ElementType) : 'a',
        href: resolved.href,
        linkProps
    };
}
